import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { DENTAL_HELP_OPTIONS } from "../../data/dental";
import { trackClick } from "../../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", practice_name: "", email: "", phone: "", locations: "", team_size: "", help_with: "", message: "" };

export const DentalLeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const inputClass =
    "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-colors duration-300";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/api/dental-lead`, form);
      trackClick("dental_lead_submitted");
      toast.success("Thanks! We'll reach out within one business day to schedule your consultation.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="dental-lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="dental-input-name" required placeholder="Name *" value={form.name} onChange={set("name")} className={inputClass} aria-label="Name" />
        <input data-testid="dental-input-practice-name" placeholder="Practice name" value={form.practice_name} onChange={set("practice_name")} className={inputClass} aria-label="Practice name" />
        <input data-testid="dental-input-email" required type="email" placeholder="Work email *" value={form.email} onChange={set("email")} className={inputClass} aria-label="Work email" />
        <input data-testid="dental-input-phone" type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputClass} aria-label="Phone" />
        <input data-testid="dental-input-locations" placeholder="Number of locations" value={form.locations} onChange={set("locations")} className={inputClass} aria-label="Number of locations" />
        <input data-testid="dental-input-team-size" placeholder="Current front desk team size" value={form.team_size} onChange={set("team_size")} className={inputClass} aria-label="Current front desk team size" />
      </div>
      <select
        data-testid="dental-input-help-with"
        value={form.help_with}
        onChange={set("help_with")}
        className={`${inputClass} ${form.help_with ? "" : "text-slate-400"}`}
        aria-label="What would you like help with?"
      >
        <option value="" disabled>What would you like help with?</option>
        {DENTAL_HELP_OPTIONS.map((o) => (
          <option key={o} value={o} className="text-slate-900">{o}</option>
        ))}
      </select>
      <textarea data-testid="dental-input-message" rows={3} placeholder="Anything we should know? (Ask about our pilot program)" value={form.message} onChange={set("message")} className={`${inputClass} resize-none`} aria-label="Message" />
      <button
        type="submit"
        disabled={sending}
        data-testid="dental-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-8 py-4 text-sm hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)] disabled:opacity-60 transition-shadow duration-300"
      >
        {sending ? "Sending…" : "Book a Free 15-Minute Consultation"} <ArrowRight size={15} />
      </button>
      <p className="text-[11px] text-center text-slate-400" data-testid="dental-phi-warning">
        HIPAA-conscious by design — please don't include patient information in this form.
      </p>
    </form>
  );
};
